import { Tag } from 'antd';
import { FiAlertCircle, FiDownload, FiFileText, FiLoader } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { EXPORT_STATE_TAG, useExportDownload } from '../../hooks/useExportDownload';
import { DataExportCompletedNotificationType, DataExportFailedNotificationType } from '../../types/notifications';
import { notificationSentAtFormattedDate } from '../../utils';

type DataExportNotificationCardProps = {
  notificationDetails: DataExportCompletedNotificationType | DataExportFailedNotificationType;
  onClick: () => void;
};

const ICON_CLASS = {
  ready: 'bg-green-100 text-green-600',
  failed: 'bg-red-100 text-red-600',
  empty: 'bg-gray-100 text-gray-500',
  unavailable: 'bg-gray-100 text-gray-500',
};

const DataExportNotificationCard = ({ notificationDetails, onClick }: DataExportNotificationCardProps) => {
  const { t } = useTranslation();
  const { state, available, downloading, download } = useExportDownload(notificationDetails);
  const tag = EXPORT_STATE_TAG[state];

  // Only a finished or no-longer-available download marks it read: a failed one stays for a retry.
  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!available) {
      onClick();
      return;
    }
    const outcome = await download();
    if (outcome === 'downloaded' || outcome === 'unavailable') onClick();
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative flex gap-4 rounded-xl border p-6 transition-all hover:shadow-lg cursor-pointer ${
        notificationDetails.isRead ? 'border-gray-200 bg-white' : 'border-blue-200 bg-blue-50'
      }`}
    >
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${ICON_CLASS[state]}`}>
        {state === 'ready' && !downloading && <FiDownload size={24} />}
        {downloading && (
          <FiLoader size={24} className="animate-spin" role="img" aria-label={t('notifications-downloading')} />
        )}
        {state === 'failed' && <FiAlertCircle size={24} />}
        {(state === 'empty' || state === 'unavailable') && <FiFileText size={24} />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="mb-1 text-lg font-semibold text-gray-900">{notificationDetails.title}</h3>
            <p className="whitespace-pre-line text-sm text-gray-600">{notificationDetails.message}</p>
          </div>
          <Tag color={tag.color}>{t(tag.labelKey)}</Tag>
        </div>

        {available && (
          <div className="mt-3">
            <button
              type="button"
              onClick={handleClick}
              disabled={downloading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              <FiDownload size={16} />
              {t('notifications-download-export-file')}
            </button>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
          <span>{notificationSentAtFormattedDate(notificationDetails.createdAt)}</span>
          {!notificationDetails.isRead && (
            <span className="rounded-full bg-blue-500 px-2 py-1 text-xs font-medium text-white">
              {t('notifications-new-badge')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataExportNotificationCard;
